import { useEffect } from 'react';

import { BrandsModel, inventoryApi, profileApi, ProfileModel } from '@pukka/api';
import { assertUtil } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { getUnleashClient } from '@pukka/unleash/client';
import { proxy, proxyMap, proxySet, withPersist } from '@pukka/valtio';

import { BrandsAssignState } from './Modal/BrandsAssign.state';

const BRAND_LIST_SIZE = 50;
const PARENT_LIST_SIZE = 500;
const GENERAL_ERROR_CODE = ['UNSPECIFIED', 'ERR_NETWORK', 'ERR_BAD_RESPONSE'];

type DataStatus = 'error' | 'success' | 'loading' | 'unloaded';

interface ParentBrand {
  id: string | number;
  name: string;
  totalBrands: number;
}

type BrandList = ParentBrand[];

interface IBrandsState extends Record<string, unknown> {
  selectedOutlet: { key: number; value: string };
  loadingParentId: number | null;
  shouldFetchMoreBrands: Record<string, boolean>;
  currentPageByParentId: Record<string, number>;
  expandedParentId: number | null;
  checkedParent: Set<number>;
  checkedBrand: Map<number, BrandsModel.BrandDetail>;
  checkedAll: boolean;
  status: {
    parentList: DataStatus;
    brandListByKeyword: DataStatus;
  };
  keyword: string;
  currentPageByKeyword: number;
  keywordType: 'brand' | 'parent';
  showBulkDeleteModal: boolean;
  showBulkMoveModal: boolean;
  isLoadingBulkAction: boolean;
  isBulkDeleteAllOutlet: boolean;
  totalChecked: { parentCount: number; brandCount: number };
  filteredParentList: BrandsModel.GetParentListResponse['results'] | undefined;
  anyChildCount: number;
  showSelectAll: boolean;
  handleChangeOutlet(outlet: ProfileModel.Outlet): void;
  handleChangeKeyword(keyword: string, type: 'brand' | 'parent'): void;
  handleFetchMoreByKeyword(): void;
  handleCheckAll(): void;
  handleUncheckAll(): void;
  handleCheckParent(id: number): void;
  handleCheckBrand(brand: BrandsModel.BrandDetail): void;
  handleToggleCollapsible(id: string | number): void;
  handleToggleBulkDeleteModal: () => void;
  handleToggleBulkDeleteAllOutletModal: () => void;
  handleBulkDelete: () => void;
  handleToggleBulkMoveModal: () => void;
  handleBulkMove: () => void;
  useBrands(): void;
  fetchParentList(expandParentId?: number, outletId?: number): void;
  fetchMoreBrandsByParent(parentId: number): void;
}

interface IBrandsPersistedState extends Record<string, unknown> {
  outlets: ProfileModel.Outlet[];
  parentList: BrandsModel.GetParentListResponse | null;
  brandListByParent: {
    [key: string]: BrandsModel.GetBrandListResponse;
  };
  brandListByKeyword: BrandsModel.GetBrandListResponse | null;
}

/* istanbul ignore next */
function handleGeneralError(e: unknown) {
  assertUtil.assertObjectIsTruthy<{
    code: string;
    message: string;
  }>(e);

  /* istanbul ignore next */
  if (GENERAL_ERROR_CODE.includes(e.code)) {
    toast.error('A technical error has occurred. Please try again.');
  } else {
    toast.error(e.message || 'A technical error has occurred. Please try again.');
  }
}

function generateBulkDeleteMessage(
  parentList: BrandsModel.GetParentListResponse['results'],
  brandList: BrandsModel.GetBrandListResponse['results'],
) {
  if (parentList.length === 1 && brandList.length === 0) {
    if (parentList[0].id === -1) {
      return 'Brands';
    }

    return `${parentList[0].name}`;
  }

  if (parentList.length === 0 && brandList.length === 1) {
    return `${brandList[0].name}`;
  }

  if (parentList.length > 1 && brandList.length === 0) {
    if (parentList.some((parent) => parent.id === -1)) {
      return 'Brands and principals';
    }

    return 'Principals';
  }

  if (parentList.length === 0 && brandList.length > 1) {
    return 'Brands';
  }

  return 'Brands and principals';
}

function generatePreselectedParent(
  checkedParent: Set<number>,
  checkedBrand: Map<number, BrandsModel.BrandDetail>,
  parentList: BrandsModel.GetParentListResponse['results'],
) {
  let preselectedParent = {
    id: -2,
    name: '',
  };

  const parentCount = checkedParent.size;
  const brandCount = checkedBrand.size;

  if (parentCount === 1 && brandCount === 0) {
    preselectedParent = parentList.find((parent) => checkedParent.has(parent.id)) || preselectedParent;
  } else if (parentCount === 0 && brandCount > 0) {
    const selectedParentIds = new Set(Array.from(checkedBrand.values(), (brand) => brand.principal_id));

    if (selectedParentIds.size === 1) {
      let parentId = selectedParentIds.values().next().value;

      if (parentId === null) {
        parentId = -1;
      }

      preselectedParent = parentList.find((parent) => parentId === parent.id) || preselectedParent;
    }
  }

  return preselectedParent;
}

function updateMap<K, V>(map: Map<K, V>, key: K, value: V) {
  const newMap = new Map(map);

  if (newMap.has(key)) {
    newMap.delete(key);
  } else {
    newMap.set(key, value);
  }

  return newMap;
}

function updateSet<T>(set: Set<T>, value: T) {
  const newSet = new Set(set);

  if (newSet.has(value)) {
    newSet.delete(value);
  } else {
    newSet.add(value);
  }

  return newSet;
}

const { proxy: BrandsPersistedState } = withPersist<IBrandsPersistedState>(
  {
    outlets: [],
    parentList: null,
    totalParent: 0,
    brandListByParent: {},
    brandListByKeyword: null,
  },
  'brands',
  {
    // expiry: 1000 * 60 * 60 * 24, // 1 day expiry
    expiry: 1000, // 1 second expiry
    session: 'user', // removed on logout
  },
);

const BrandsState: IBrandsState = proxy({
  selectedOutlet: { key: 0, value: '' },
  loadingParentId: null,
  shouldFetchMoreBrands: {},

  currentPageByParentId: {},

  status: {
    parentList: 'unloaded',
    brandListByKeyword: 'unloaded',
  },
  expandedParentId: null,

  checkedParent: proxySet<number>(),
  checkedBrand: proxyMap<number, BrandsModel.BrandDetail>(),
  checkedAll: false,

  keyword: '',
  currentPageByKeyword: 1,
  keywordType: 'brand',

  showBulkDeleteModal: false,
  showBulkMoveModal: false,
  isBulkDeleteAllOutlet: false,

  isLoadingBulkAction: false,

  get totalChecked() {
    let parentCount = BrandsState.checkedParent.size;

    if (BrandsState.checkedParent.has(-1)) {
      parentCount -= 1;
    }

    let brandsInParentCount = 0;

    for (const parentId of BrandsState.checkedParent) {
      const parent = BrandsPersistedState.parentList?.results.find((p) => p.id === parentId || (p.id === null && parentId === -1));

      if (parent) {
        brandsInParentCount += parent.brand_count;
      }
    }

    const brandCount = BrandsState.checkedBrand.size;

    return {
      parentCount,
      brandCount: brandsInParentCount + brandCount,
    };
  },

  get filteredParentList() {
    if (BrandsState.keyword && BrandsState.keywordType === 'parent') {
      return BrandsPersistedState.parentList?.results.filter((parent) =>
        parent.name.toLowerCase().includes(BrandsState.keyword.toLowerCase()),
      );
    }

    return BrandsPersistedState.parentList?.results;
  },

  get anyChildCount() {
    if (BrandsState.checkedParent.size > 0) {
      for (const parentId of BrandsState.checkedParent) {
        const parent = BrandsPersistedState.parentList?.results.find((p) => p.id === parentId && p.id !== -1);

        if (parent && parent.brand_count > 0) {
          return parent.brand_count;
        }
      }
    }

    for (const brand of BrandsState.checkedBrand.values()) {
      if (brand.item_count > 0) {
        return brand.item_count;
      }
    }

    return 0;
  },

  get showSelectAll() {
    const { brandListByKeyword } = BrandsPersistedState;
    const brandList = brandListByKeyword?.results;

    const { status, keyword, keywordType, filteredParentList } = BrandsState;

    const brandsNotFound = keyword && keywordType === 'brand' && status.brandListByKeyword === 'success' && brandList?.length === 0;

    const parentsNotFound = keyword && keywordType === 'parent' && status.parentList === 'success' && filteredParentList?.length === 0;

    return !brandsNotFound && !parentsNotFound;
  },

  async handleChangeKeyword(keyword: string, type: 'brand' | 'parent') {
    if (!!keyword && !!BrandsState.expandedParentId) {
      BrandsState.expandedParentId = null;
    }

    BrandsState.status.brandListByKeyword = 'loading';
    BrandsState.keyword = keyword;
    BrandsState.currentPageByKeyword = 1;
    BrandsState.keywordType = type;

    if (type === 'brand') {
      try {
        const data = await inventoryApi.getBrandList(BrandsState.selectedOutlet.key, {
          keyword,
          deep: true,
          size: BRAND_LIST_SIZE,
        });

        BrandsPersistedState.brandListByKeyword = data;
        BrandsState.status.brandListByKeyword = 'success';
      } catch (error) {
        // TODO: handle error
        /* istanbul ignore next */
        handleGeneralError(error);
      }
    }
  },
  async handleFetchMoreByKeyword() {
    BrandsState.status.brandListByKeyword = 'loading';

    try {
      const data = await inventoryApi.getBrandList(BrandsState.selectedOutlet.key, {
        keyword: BrandsState.keyword,
        deep: true,
        page: BrandsState.currentPageByKeyword + 1,
        size: BRAND_LIST_SIZE,
      });

      if (BrandsPersistedState.brandListByKeyword) {
        BrandsPersistedState.brandListByKeyword.completed = data.completed;
        BrandsPersistedState.brandListByKeyword.results?.push(...data.results);
      }

      BrandsState.currentPageByKeyword += 1;
      BrandsState.status.brandListByKeyword = 'success';
    } catch (error) {
      // TODO: handle error
      /* istanbul ignore next */
      handleGeneralError(error);
    }
  },
  async handleChangeOutlet(outlet: ProfileModel.Outlet) {
    const outletId = outlet.id as unknown as number;

    BrandsState.selectedOutlet = {
      key: outletId,
      value: outlet.name as unknown as string,
    };

    BrandsState.handleUncheckAll();
    BrandsState.fetchParentList(-1, outletId);
  },
  handleCheckAll() {
    if (BrandsState.checkedAll) {
      BrandsState.checkedParent.clear();
      BrandsState.checkedBrand.clear();
      BrandsState.checkedAll = false;
    } else {
      const allParentIds = BrandsPersistedState.parentList?.results.map((parent) => parent.id);

      BrandsState.checkedParent = proxySet(allParentIds);
      BrandsState.checkedBrand.clear();
      BrandsState.checkedAll = true;
    }
  },
  handleUncheckAll() {
    BrandsState.checkedParent.clear();
    BrandsState.checkedBrand.clear();
    BrandsState.checkedAll = false;
  },
  handleCheckParent: async (id: number) => {
    const newParentSet = updateSet(BrandsState.checkedParent, id);

    // Uncheck all if a parent is unchecked
    if (!newParentSet.has(id)) {
      BrandsState.checkedAll = false;
    }

    const newBrandSet = new Map(BrandsState.checkedBrand);
    const brandsByParent = BrandsPersistedState.brandListByParent[id];

    for await (const brand of brandsByParent?.results || []) {
      // remove checked brands if parent is checked
      if (newParentSet.has(id)) {
        newBrandSet.delete(brand.id);
      }
    }

    BrandsState.checkedBrand = proxyMap(newBrandSet);
    BrandsState.checkedParent = proxySet(newParentSet);
  },
  handleCheckBrand(brand: BrandsModel.BrandDetail) {
    BrandsState.checkedBrand = proxyMap(updateMap(BrandsState.checkedBrand, brand.id, brand));
  },
  async handleToggleCollapsible(id: number) {
    // collapse if already open
    if (BrandsState.expandedParentId === id) {
      BrandsState.expandedParentId = null;

      return;
    }

    BrandsState.expandedParentId = id;

    if (BrandsPersistedState.brandListByParent[id]) {
      return;
    }

    BrandsState.loadingParentId = id;

    try {
      const res = await inventoryApi.getBrandList(BrandsState.selectedOutlet.key, { principal_id: id, deep: true, size: BRAND_LIST_SIZE });

      BrandsPersistedState.brandListByParent[id] = res;
      BrandsState.shouldFetchMoreBrands[id] = !res.completed;
      BrandsState.loadingParentId = null;
      BrandsState.currentPageByParentId[id] = 1;
    } catch (e) {
      // TODO: handle error
      /* istanbul ignore next */
      handleGeneralError(e);
    }
  },

  fetchMoreBrandsByParent: async (parentId: number) => {
    if (BrandsPersistedState.brandListByParent[parentId].total_count <= BrandsPersistedState.brandListByParent[parentId].results.length) {
      return;
    }

    BrandsState.loadingParentId = parentId;

    const currentPage = BrandsState.currentPageByParentId[parentId] ?? 1;
    const nextPage = currentPage + 1;

    try {
      const res = await inventoryApi.getBrandList(BrandsState.selectedOutlet.key, {
        principal_id: parentId,
        deep: true,
        page: nextPage,
        size: BRAND_LIST_SIZE,
      });

      BrandsPersistedState.brandListByParent[parentId].completed = res.completed;

      BrandsPersistedState.brandListByParent[parentId].results.push(...res.results);
      BrandsState.shouldFetchMoreBrands[parentId] = !res.completed;
      BrandsState.loadingParentId = null;
      BrandsState.currentPageByParentId[parentId] = nextPage;
    } catch (e) {
      // TODO: handle error
      /* istanbul ignore next */
      handleGeneralError(e);
    }
  },

  fetchParentList: async (expandParentId = -1, outletId = BrandsState.selectedOutlet.key) => {
    try {
      const unleashClient = await getUnleashClient();
      const variant = unleashClient.getVariantWithFallback('backoffice-principal-brands');
      const config = JSON.parse(variant?.payload?.value ?? '{}');
      const parentListSize = config?.principal_size || PARENT_LIST_SIZE;

      BrandsState.expandedParentId = null;

      if (BrandsState.keyword) {
        BrandsState.keyword = '';
        BrandsState.currentPageByKeyword = 1;

        const searchInput = window.document?.getElementById('list-search') as HTMLInputElement | null;

        /* istanbul ignore next */
        if (searchInput) {
          searchInput.value = '';
        }
      }

      BrandsPersistedState.parentList = null;
      BrandsPersistedState.brandListByParent = {};
      BrandsPersistedState.brandListByKeyword = null;

      BrandsState.status.parentList = 'loading';

      const response = await inventoryApi.getParentList(outletId, { deep: true, size: parentListSize });

      BrandsPersistedState.parentList = response;
      BrandsState.status.parentList = 'success';

      if (response.results.length > 0) {
        const expandId = expandParentId === null ? -1 : expandParentId;
        const parentToExpand = response.results.find((parent) => parent.id === expandId);

        if (parentToExpand) {
          // fetch parent brands
          const res = await inventoryApi.getBrandList(outletId, { principal_id: expandId, deep: true, size: BRAND_LIST_SIZE });

          BrandsPersistedState.brandListByParent[expandId] = res;
          BrandsState.expandedParentId = expandId;
        }
      }
    } catch (error: unknown) {
      // TODO: handle error
      /* istanbul ignore next */
      handleGeneralError(error);
    }
  },

  handleToggleBulkDeleteAllOutletModal: () => {
    BrandsState.isBulkDeleteAllOutlet = true;
    BrandsState.showBulkDeleteModal = !BrandsState.showBulkDeleteModal;
  },

  handleToggleBulkDeleteModal: () => {
    BrandsState.isBulkDeleteAllOutlet = false;
    BrandsState.showBulkDeleteModal = !BrandsState.showBulkDeleteModal;
  },

  handleBulkDelete: async () => {
    const parentIds = Array.from(BrandsState.checkedParent);
    const brandIds = Array.from(BrandsState.checkedBrand.keys());

    BrandsState.isLoadingBulkAction = true;

    try {
      await inventoryApi.bulkDelete(BrandsState.selectedOutlet.key, {
        brand_ids: brandIds,
        principal_ids: parentIds as number[],
        is_saved_to_all_outlets: BrandsState.isBulkDeleteAllOutlet,
      });

      const subject = generateBulkDeleteMessage(
        BrandsPersistedState.parentList?.results.filter((parent) => parentIds.includes(parent.id)) || /* istanbul ignore next */ [],
        Array.from(BrandsState.checkedBrand.values()),
      );

      toast.success(`${subject} successfully deleted.`);

      BrandsState.checkedParent.clear();
      BrandsState.checkedBrand.clear();
      BrandsState.checkedAll = false;
      BrandsState.showBulkDeleteModal = false;
      BrandsState.fetchParentList(-1);
    } catch (error) {
      // TODO: handle error test
      /* istanbul ignore next */
      handleGeneralError(error);
    } finally {
      BrandsState.isLoadingBulkAction = false;
    }
  },

  handleToggleBulkMoveModal: () => {
    if (!BrandsState.showBulkMoveModal) {
      const preselectedParent = generatePreselectedParent(
        BrandsState.checkedParent,
        BrandsState.checkedBrand,
        BrandsPersistedState.parentList?.results || /* istanbul ignore next */ [],
      );

      BrandsAssignState.initParentList(preselectedParent, () => {
        BrandsState.showBulkMoveModal = true;
      });
    } else {
      BrandsState.showBulkMoveModal = false;
    }
  },

  handleBulkMove: async () => {
    const parentIds = Array.from(BrandsState.checkedParent);
    const brandIds = Array.from(BrandsState.checkedBrand.keys());
    let targetId = BrandsAssignState.selectedParent!.id as number | null;

    BrandsState.isLoadingBulkAction = true;

    if (targetId === -1) {
      targetId = null;
    }

    try {
      await inventoryApi.bulkMove(BrandsState.selectedOutlet.key, {
        principal_ids: parentIds,
        brand_ids: brandIds,
        target_principal_id: targetId,
      });

      toast.success('Brands have been successfully modified.');

      BrandsState.checkedParent.clear();
      BrandsState.checkedBrand.clear();
      BrandsState.checkedAll = false;
      BrandsState.handleToggleBulkMoveModal();
      BrandsState.fetchParentList(targetId || -1);
    } catch (error) {
      // TODO: handle error test
      /* istanbul ignore next */
      handleGeneralError(error);
    } finally {
      BrandsState.isLoadingBulkAction = false;
    }
  },

  /* istanbul ignore next */
  useBrands() {
    const { data: profile, status: profileStatus } = profileApi.useProfile();

    useEffect(() => {
      if (profileStatus === 'success' && profile.outlets && !BrandsState.selectedOutlet.value) {
        BrandsPersistedState.outlets = profile.outlets;

        const outletId = profile.outlets[0].id as unknown as number;

        BrandsState.selectedOutlet = {
          key: outletId,
          value: profile.outlets[0].name as unknown as string,
        };

        if (!BrandsPersistedState.parentList) {
          BrandsState.fetchParentList(-1, outletId);
        }
      }
    }, [profile, profileStatus]);
  },
});

export {
  BrandsPersistedState,
  BrandsState,
  type IBrandsPersistedState,
  type IBrandsState,
  type BrandList,
  generateBulkDeleteMessage,
  generatePreselectedParent,
};
